import * as React from 'react';
import { Route, Routes } from 'react-router-dom';
import { RoboView } from '@app/RoboView/RoboView';
import { GeneralSettings } from '@app/Settings/General/GeneralSettings';
import { ProfileSettings } from '@app/Settings/Profile/ProfileSettings';
import { NotFound } from '@app/NotFound/NotFound';
import Dashboard from '@app/Dashboard/Dashboard';
import SessionsDashboard from '@app/Sessions/SessionsDashboard';
import SessionDetails from '@app/Sessions/SessionDetails';
import ReportingDashboard from '@app/Reporting/ReportingDashboard';
import AcquisitionReportingDashboard from '@app/Reporting/AcquisitionReportingDashboard';
import UserGridReportingDashboard from '@app/Reporting/UserGridReportingDashboard';
import SessionEventForwardingMonitor from '@app/Forwarding/SessionEventForwardingMonitor';
import CoreEntities from '@app/CoreEntities/CoreEntities';
import QueueManager from '@app/QueueManager/QueueManager';
import QueueManagerHealth from '@app/QueueManager/QueueManagerHealth';

export interface IAppRoute {
  label?: string;
  element: React.ReactElement;
  exact?: boolean;
  path: string;
  title: string;
  routes?: undefined;
}

export interface IAppRouteGroup {
  label: string;
  routes: IAppRoute[];
}

export type AppRouteConfig = IAppRoute | IAppRouteGroup;

const routes: AppRouteConfig[] = [
  {
    element: <Dashboard />,
    exact: true,
    label: 'Dashboard',
    path: '/',
    title: 'DwellBot | RoboView',
  },
  {
    element: <SessionsDashboard />,
    exact: true,
    label: 'Sessions',
    path: '/sessions',
    title: 'DwellBot | Sessions',
  },
  {
    element: <ReportingDashboard />,
    exact: true,
    label: 'Reporting',
    path: '/reporting',
    title: 'DwellBot | Reporting',
  },
  {
    element: <AcquisitionReportingDashboard />,
    exact: true,
    label: 'Acquisition Reporting',
    path: '/reporting/acquisition',
    title: 'DwellBot | Acquisition Reporting',
  },
  {
    element: <UserGridReportingDashboard />,
    exact: true,
    label: 'User Reporting',
    path: '/reporting/users',
    title: 'DwellBot | User Reporting',
  },
  {
    element: <SessionEventForwardingMonitor />,
    exact: true,
    label: 'Delivery Monitor',
    path: '/forwarding',
    title: 'DwellBot | Delivery Monitor',
  },
  {
    element: <SessionDetails />,
    exact: true,
    path: '/sessions/:sessionId',
    title: 'DwellBot | Session Details',
  },
  {
    element: <RoboView />,
    exact: true,
    label: 'Robots',
    path: '/bot',
    title: 'DwellBot | RoboView',
  },
  {
    element: <RoboView />,
    exact: true,
    path: '/bot/:robotId',
    title: 'DwellBot | RoboView',
  },
  {
    element: <CoreEntities />,
    exact: true,
    label: 'Core',
    path: '/core',
    title: 'DwellBot | Core Management',
  },
  {
    element: <QueueManager />,
    exact: true,
    label: 'Queue Manager',
    path: '/queue-manager',
    title: 'DwellBot | Queue Manager',
  },
  {
    element: <QueueManagerHealth />,
    exact: true,
    label: 'Queue Health',
    path: '/queue-manager/health',
    title: 'DwellBot | Queue Health',
  },
  {
    label: 'Settings',
    routes: [
      {
        element: <GeneralSettings />,
        exact: true,
        label: 'General',
        path: '/settings/general',
        title: 'DwellBot | General Settings',
      },
      {
        element: <ProfileSettings />,
        exact: true,
        label: 'Profile',
        path: '/settings/profile',
        title: 'DwellBot | Profile Settings',
      }
    ],
  },
];

const flattenedRoutes: IAppRoute[] = routes.reduce(
  (flattened, route) => [...flattened, ...(route.routes ? route.routes : [route])],
  [] as IAppRoute[],
);

const AppRoutes = (): React.ReactElement => (
  <Routes>
    {flattenedRoutes.map(({ path, element }, idx) => (
      <Route path={path} element={element} key={idx} />
    ))}
    <Route element={<NotFound />} />
  </Routes>
);

export { AppRoutes, routes };
